import { Button, Dialog } from 'ui';
import Dropzone from 'core/modules/components/Dropzone/Dropzone';
import UploadedFile from 'core/modules/components/UploadedFile/UploadedFile';
import UploadedProgress from 'core/modules/components/UploadedProgress/UploadedProgress';
import UploadedSelectedFile from 'core/modules/components/UploadedSelectedFile/UploadedSelectedFile';
import { getFilenameUrl, validateUrl } from 'core/utils/files';
import React from 'react';
import { useTranslation } from 'react-i18next';

// NOTA:
// --------------------------------------------------------------------------------------------------------
// Para subir los archivos al servidor usar las funcion situada en app/services/upload
// import { uploadFileContent } from 'app/services/upload'
// --------------------------------------------------------------------------------------------------------

type UploadProps = {
  accept?: '' | 'image' | 'audio' | 'video' | 'zip';
  accepts?: string;
  canNewVersion?: boolean;
  disabled?: boolean;
  file?: File;
  fileDate?: string;
  getFile: (file: File | undefined) => void;
  hasImage?: boolean;
  isUrl?: boolean;
  isVisibleImg?: boolean;
  mimeType?: string[];
  onChange?: (value: string) => void;
  onDeleteFile?: () => void;
  progress?: number;
  size?: string;
  subTitle?: string;
  title?: string;
  url?: string;
};

function Upload({
  accept,
  accepts,
  canNewVersion = false,
  disabled = false,
  file,
  fileDate,
  getFile,
  hasImage = false,
  isUrl = true,
  isVisibleImg = false,
  mimeType = [],
  onChange = () => null,
  onDeleteFile,
  progress,
  size,
  subTitle = '',
  title = '',
  url = '',
}: UploadProps): JSX.Element {
  const { t } = useTranslation(['common', 'actions']);
  const [openDelete, setOpenDelete] = React.useState<boolean>(false);
  const fileName = getFilenameUrl(url);
  const fileInputRef = React.createRef<HTMLInputElement>();

  function openFileDialog(): void {
    if (disabled) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }

  function onSelectFiles(files: File[]): void {
    if (files.length > 0) {
      getFile(files[0]);
      onChange(files[0].name);
    }
  }

  function onDelete(): void {
    getFile(undefined);
  }

  function onDeleteFileConfirme(): void {
    if (onDeleteFile) {
      setOpenDelete(true);
    } else {
      getFile(undefined);
      onChange('');
    }
  }

  function onDeleteSuccess(): void {
    if (onDeleteFile) {
      onDeleteFile();
    }
    getFile(undefined);
    onChange('');
    setOpenDelete(false);
  }

  function _renderNotFile(): JSX.Element {
    if (progress && progress < 100) {
      return <UploadedProgress progress={progress} subTitle={subTitle} title={title} />;
    }

    if (file && file.name) {
      return <UploadedSelectedFile file={file} hasImage={hasImage} onDelete={onDelete} />;
    }

    return (
      <Dropzone
        accept={accept}
        accepts={accepts}
        disabled={disabled}
        fileInputRef={fileInputRef}
        mimeType={mimeType}
        onSelectFiles={onSelectFiles}
        openFileDialog={openFileDialog}
        subTitle={subTitle}
        title={title}
      />
    );
  }

  return validateUrl(url) && isUrl ? (
    <>
      <UploadedFile fileName={fileName} fileDate={fileDate} isVisibleImg={isVisibleImg} onDelete={onDeleteFileConfirme} size={size} url={url} />

      {canNewVersion && (
        <>
          <div className="uploaded-file__version mt-4">
            <Button variant="neutral-soft" leftIcon="upload-file" onClick={openFileDialog} text={t('common:file_upload_new_version')} isFullWidth />
          </div>

          <div style={{ display: 'none' }}>{_renderNotFile()}</div>
        </>
      )}

      <Dialog
        isOpen={openDelete}
        onClose={() => setOpenDelete(false)}
        onSuccess={onDeleteSuccess}
        title={t('common:upload_delete_file_title')}
        text={t('common:upload_delete_file_subtitle')}
        buttonTextSuccess={t('actions:Confirm')}
        buttonTextCancel={t('actions:Cancel')}
        size="s"
      />
    </>
  ) : (
    _renderNotFile()
  );
}

export default Upload;
